import { ReactElement, useEffect, useRef, useState } from 'react';
import { Form, Formik, FormikProps, getIn } from 'formik';
import FormContainer from '@pages/FormContainer';
import ReactGA from 'react-ga';
import { defaultForm, InstalmentMetaData, InstalmentSchema, iInstalmentFormValues, getSubmissionPayload } from '@services/InstalmentService';
import { FormNavFooter, FormLoading, Alert, ProgressBar } from '@components/index';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { FormType } from '@constants';
import { iToolTips, iCountry, iApiError, iFormNavActions, iPartialFormData } from '@interfaces/interfaces';

import { getDateForValidateDebtor, getStringIndex, navToError, navigateToError, navigateToNextStep, setCurrentFormikValues, setFormDataValues } from '@utils';

import ErrorContent from '@components/Content/AlertContent/ErrorContent';

import { getCountryList, getPartialForm, getTooltips, savePartialForm, submitSavedPartialForm } from '@api';

import VicFinesContext from '../VicFinesFormContext';
import { InstalmentErrorPage, InstalmentStep0, InstalmentStep1, InstalmentStep10, InstalmentStep11, InstalmentStep12, InstalmentStep13, InstalmentStep14, InstalmentStep15, InstalmentStep2, InstalmentStep3, InstalmentStep4, InstalmentStep5, InstalmentStep6, InstalmentStep7, InstalmentStep8, InstalmentStep9 } from '.';

import IssueIcon from '../../../images/issue.svg';

const {
  REACT_APP_SITECORE_API_ENDPOINT: API_URL,
  REACT_APP_PATH_INSTALMENT: PATH_INSTALMENT,
} = process.env;

function Instalment(): ReactElement {
  const formikValuesRef = useRef({} as FormikProps<iInstalmentFormValues>);
  const location = useLocation();
  const navigate = useNavigate();
  const pathElementArray = location.pathname.split('/');
  const stepIndex = getStringIndex(pathElementArray, 'step');
  const stepIndexDiff = stepIndex - pathElementArray.length;
  // example returned [step0,3224242] or [step0]
  const stepArray = pathElementArray.slice(stepIndexDiff);
  // example returned 0
  const stepIndexInPath = getStringIndex(stepArray, 'step');
  // always get the element contain step like step0
  const getStepInPath = stepArray[stepIndexInPath];
  const pathNameLessLastSegment = stepIndex ? location.pathname.split('/').slice(0, stepIndexDiff).join('/') : location.pathname;

  const currentStep = location.pathname === `${PATH_INSTALMENT}` || location.pathname === `${PATH_INSTALMENT}/` ? 'step0' : `${getStepInPath}`;

  const [loading, setLoading] = useState(true);
  const [countryList, setCountryList] = useState<iCountry[]>([]);
  const [tooltips, setTooltips] = useState<iToolTips>({} as iToolTips);
  const [apiError, setApiError] = useState<iApiError>({} as iApiError);
  const [activeError, setActiveError] = useState(false);

  const partialFormAttachmentFields = ['SupportingDocumentsUpfrontPayment', 'SupportingFinancialDocuments'];
  const arrayFieldsToBeCleared = [
    'ActionsTakenOnFine',
    'obligations',
    'infringementFineArray',
    'enteredObligationsArray',
    'InfringementNums',
    'AgencyNames',
    'AOffence',
    'DAOffence',
    'MoreInfoAboutMatters'
  ];

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const [countryResults, tooltipResults] = await Promise.all([getCountryList(), getTooltips('fines.InstalmentForm')]);
        setCountryList(countryResults);
        setTooltips(tooltipResults);
        setLoading(false);
      } catch (error) {
        navigateToError();
      }
    };
    fetchData();
    const handlePopstate = () => {
      const isSubmitted = formikValuesRef.current && formikValuesRef.current.values ? formikValuesRef.current.values?.submitted : false;

      if (window.location.pathname === '/Pay-by-instalments/Instalment-Form/step14' || isSubmitted) {
        navigate({
          pathname: '/Pay-by-instalments/Instalment-Form/step15',
          search: location.search,
        });
      }
      // if ((window.location.pathname === '/Pay-by-instalments/Instalment-Form/step2') && !isSubmitted) {
      //   navigate({
      //     pathname: '/Pay-by-instalments/Instalment-Form/step3',
      //     search: location.search,
      //   });
      // }
      if ((window.location.pathname === '/Pay-by-instalments/Instalment-Form/step3') && !isSubmitted) {
        navigate({
          pathname: '/Pay-by-instalments/Instalment-Form/step4',
          search: location.search,
        });
      }
      if ((window.location.pathname === '/Pay-by-instalments/Instalment-Form/step4') && !isSubmitted && formikValuesRef.current.values.SaveYesNo === 'true') {
        navigate({
          pathname: '/Pay-by-instalments/Instalment-Form/step5',
          search: location.search,
        });
      }
      if ((window.location.pathname === '/Pay-by-instalments/Instalment-Form/error') && !isSubmitted) {
        navigate({
          pathname: '/Pay-by-instalments/Instalment-Form/error',
          search: location.search,
        });
      }
      if ((window.location.pathname === '/Pay-by-instalments/Instalment-Form/step1') && !isSubmitted && formikValuesRef && formikValuesRef.current) {
          formikValuesRef.current.setFieldValue('OBLIGATION_NUMBER', '');
          formikValuesRef.current.setFieldTouched('OBLIGATION_NUMBER');
          formikValuesRef.current.setFieldValue('OFFENCE_DATE', '');
          formikValuesRef.current.setFieldValue('DRIVER_LICENCE', '');
        }
      };
    window.addEventListener('popstate', handlePopstate);
    // document.addEventListener('keyup', redirectUrl);
    return () => {
      window.removeEventListener('popstate', handlePopstate);
    //   document.removeEventListener('keydown', redirectUrl);
    };
  }, []);

  const postObligation = async (formikContext: any): Promise<void> => {
    setLoading(true);
    // remove whitespaces before and after obligation number
    const trimmedObligationNumber = formikContext.values.OBLIGATION_NUMBER.trim();
    formikValuesRef.current.setFieldValue('OBLIGATION_NUMBER', trimmedObligationNumber);
    const payload = {
      obligationNumber: trimmedObligationNumber,
    };
    const url = `${API_URL}sitecore/Instalment/ValidateObligation`;
    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      setLoading(false);
      if (response.status === 400 && responseBody.Error) {
        setApiError(responseBody);
        return;
      }
      if (response.status === 422 && responseBody.Error) {
        setApiError(responseBody);
        return;
      }
      if (response.status === 200) {
        let nextPageUrl = `${pathNameLessLastSegment}${'/step3'}`;
        if (responseBody.obligationNumber) {
          formikValuesRef.current.setFieldValue('OBLIGATION_NUMBER', responseBody.obligationNumber);
        }
        if (responseBody.IsIndividual === true) {
          formikValuesRef.current.setFieldValue('isDebtorTypeIndividual', responseBody.IsIndividual);
        } else if (responseBody.IsIndividual === false) {
          formikValuesRef.current.setFieldValue('isDebtorTypeCompany', true);
        }
        if (responseBody.IsNominatable === true) {
          formikValuesRef.current.setFieldValue('isNominatable', responseBody.IsNominatable);
        } else {
          formikValuesRef.current.setFieldValue('isNominatable', responseBody.IsNominatable);
        }
        if (!responseBody.IsIndividual && responseBody.IsNominatable) {
          nextPageUrl = `${pathNameLessLastSegment}${'/step2'}`;
        }
        setApiError({
          Error: '',
        });
        navigate(nextPageUrl);
        window.scrollTo(0, 0);
        return;
      }
      navigateToError();
    } catch (error) {
      navigateToError();
    }
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const checkExistingForm = async (formikContext: any): Promise<void> => {
    setLoading(true);
    const payload = {
      ObligationNO: formikContext.values.OBLIGATION_NUMBER,
      formType: 'PaymentArrangement',
    };
    const url = `${API_URL}sitecore/PartialForms/CheckingExistingForm`;

    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      if (response.status === 200) {
        if (responseBody.PartialFormID) formikValuesRef.current.setFieldValue('PartialFormID', responseBody.PartialFormID);
        if (responseBody.DebtorID) formikValuesRef.current.setFieldValue('DebtorID', responseBody.DebtorID, formikContext);
        if (responseBody.CheckingExisting === 'no' && responseBody.CanSave) {
          setLoading(false);
          navigate(`${pathNameLessLastSegment}/step4`);
          window.scrollTo(0, 0);
          return;
        }
        if (responseBody.PartialFormID) {
          getPartialForm(responseBody.PartialFormID)
            .then((resBody) => {
              if (typeof resBody === 'string') {
                if (resBody === 'Unauthorised') {
                  navigateToError();
                }
              } else {
                const formData = JSON.parse(resBody.FormData);
                const keys = Object.keys(resBody);
                keys.forEach((key) => {
                  if (key === 'FormData') {
                    setFormDataValues(formData, formikValuesRef.current.values.FormData, formikValuesRef, 'FormData');
                    setCurrentFormikValues(formData, formikValuesRef.current.values, formikValuesRef);
                  } else if (resBody[key]) {
                    formikContext.setFieldValue(`${key}`, resBody[key]);
                  }
                });
              }
              setLoading(false);
            })
            .catch((e) => console.log(e));
        } else {
          setLoading(false);
        }
        navigate(`${pathNameLessLastSegment}/step5`);
        window.scrollTo(0, 0);
      }
    } catch (error) {
      navigateToError();
    }
  };
  const validateDebtor = async (formikContext: any): Promise<void> => {
    window.scrollTo(0, 0);
    setLoading(true);
    // Backend api is checking date time for some reasons, time need to be 14:00:00
    const getOffenceDate = formikContext.values.OFFENCE_DATE && getDateForValidateDebtor(formikContext.values.OFFENCE_DATE.toLocaleDateString('en-AU'));
    const getHearingDate = formikContext.values.HEARING_DATE && getDateForValidateDebtor(formikContext.values.HEARING_DATE.toLocaleDateString('en-AU'));
    const getDOBDate = formikContext.values.DOB && getDateForValidateDebtor(formikContext.values.DOB.toLocaleDateString('en-AU'));
    const payload = {
      dateOfBirth: getDOBDate,
      hearingDate: getHearingDate,
      licenceNumber: formikContext.values.DRIVER_LICENCE,
      obligationNumber: formikContext.values.OBLIGATION_NUMBER,
      offenceDate: getOffenceDate,
      registrationNumber: formikContext.values.VEHICLE_REGISTRATION,
    };
    const url = `${API_URL}sitecore/Instalment/ValidateDebtor`;
    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      setLoading(false);
      if (response.status === 400 && responseBody.Error) {
        setApiError(responseBody);
        return;
      }
      if (response.status === 200) {
        setApiError({
          Error: '',
        });
        if (responseBody === '/step3') {
          checkExistingForm(formikContext);
          navigate(`${location.pathname.split('/').slice(0, -1).join('/')}/step4`);
        }
        if (responseBody === '/step8' || responseBody === '/step9') {
          navigate(`${location.pathname.split('/').slice(0, -1).join('/')}step5`);
          window.scrollTo(0, 0);
        }
      }
    } catch (error) {
      navigateToError();
    }
  };

  const resetPFormDataValues = (partialFormApi: any, formikObject: any) => {
    if (partialFormApi && formikObject) {
      // clone data format like objectkey: [{},{}]
      if (
        Array.isArray(partialFormApi)
        && partialFormApi[0]
        && typeof partialFormApi[0] === 'object'
        && Array.isArray(formikObject)
        && (formikObject.length !== partialFormApi.length || partialFormApi.length === 0)
      ) {
        partialFormApi.forEach((val) => {
          type dynamicObj = {
            [key: string]: string;
          };
          const temp = {} as dynamicObj;
          Object.keys(val).forEach((key) => {
            temp[key] = val[key];
          });
          formikObject.push(temp);
        });
      }
      // handle scenario if formikObject is empty array , it's for ObjectKey:[] or string[]
      if (
        Array.isArray(partialFormApi)
        && partialFormApi[0]
        && typeof partialFormApi[0] === 'string'
        && Array.isArray(formikObject)
        && (formikObject.length !== partialFormApi.length || partialFormApi.length === 0)
      ) {
        partialFormApi.forEach((val) => {
          formikObject.push(val);
        });
      }
      if (partialFormApi && formikObject) {
        Object.keys(partialFormApi).forEach((key) => {
          if (key in formikObject) {
            if (typeof partialFormApi[key] === 'object' && !(partialFormApi[key] instanceof Date)) {
              if (partialFormAttachmentFields.includes(key) || arrayFieldsToBeCleared.includes(key)) {
                // eslint-disable-next-line no-param-reassign
                formikObject[key] = [];
              }
              resetPFormDataValues(partialFormApi[key], formikObject[key]);
            } else {
              // eslint-disable-next-line no-param-reassign
              formikObject[key] = partialFormApi[key];
            }
          }
        });
      }
    }
  };

  const createPartialForm = async (formikContext: any): Promise<void> => {
    if (formikContext.values.SaveYesNo === 'false' || getIn(formikContext.values, 'PartialFormID')) {
      navigateToNextStep(navigate, location, 'next');
      return;
    }
    setLoading(true);
    const { values } = formikContext;
    resetPFormDataValues(values, formikContext.values.FormData);
    const payload = {
      Attachments: '',
      CreateDate: '',
      DebtorID: `${formikContext.values.DebtorID}`,
      EMRemeinderSent: false,
      Email: formikContext.values.saveEmail,
      ExpiryDate: '',
      FormData: JSON.stringify({ a: '1', ...formikContext.values.FormData }),
      FormTypeID: 1,
      ID: 0,
      IsSubscribedEM: !!formikContext.values.saveEmail,
      IsSubscribedSMS: !!formikContext.values.saveMobile,
      Mobile: formikContext.values.saveMobile,
      Obligations: [
        {
          ID: 0,
          ObligationNO: formikContext.values.OBLIGATION_NUMBER,
          IsPrimary: true,
        },
      ],
      SMSRemeinderSent: false,
    };
    const url = `${API_URL}sitecore/PartialForms/CreatePartialForm`;
    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      setLoading(false);
      if (response.status === 200) {
        if (responseBody.ID) {
          formikValuesRef.current.setFieldValue('PartialFormID', responseBody.ID);
          formikValuesRef.current.setFieldValue('Obligations', responseBody.Obligations);
        }
        navigateToNextStep(navigate, location, 'next');
        return;
      }
      navigateToError();
    } catch (error) {
      console.log(error);
    }
  };
  const updatePartialFormData = async (formikContext: any): Promise<boolean> => {
    const { values } = formikContext;
    resetPFormDataValues(values, formikContext.values.FormData);
    const partialformPayload: iPartialFormData = {
      ID: formikContext.values.PartialFormID,
      Obligations: formikContext.values.Obligations,
      Attachments: [],
      FormData: JSON.stringify(formikContext.values.FormData), // {a: '1'},formikContext.values.FormData
      DebtorID: formikContext.values.DebtorID,
      FormTypeID: 1,
      Email: formikContext.values.saveEmail,
      Mobile: formikContext.values.saveMobile,
      IsSubscribedEM: !!formikContext.values.saveEmail,
      IsSubscribedSMS: !!formikContext.values.saveMobile,
      EMRemeinderSent: formikContext.values.EMRemeinderSent,
      SMSRemeinderSent: formikContext.values.SMSRemeinderSent,
      CreateDate: formikContext.values.CreateDate,
      ExpiryDate: formikContext.values.ExpiryDate,
    };
    try {
      const response = await savePartialForm(partialformPayload);
      return response;
    } catch (error) {
      console.log(error);
      return false;
    }
  };
  const step6NextPage = (formikContext: any) => {
    window.scrollTo(0, 0);
    if (formikContext.values.SaveYesNo === 'true') {
      updatePartialFormData(formikContext);
    }
    if (formikContext.values.NoInfoApplies) {
      if (formikContext.values.isDebtorTypeIndividual) {
        navigate(`${location.pathname.split('/').slice(0, -1).join('/')}/step8`);
      } else {
        navigate(`${location.pathname.split('/').slice(0, -1).join('/')}/step9`);
      }
    } else {
      navigateToNextStep(navigate, location, 'next');
    }
  };
  const step7NextPage = (formikContext: any) => {
    window.scrollTo(0, 0);
    if (formikContext.values.SaveYesNo === 'true') {
      updatePartialFormData(formikContext);
    }
    if (formikContext.values.isDebtorTypeIndividual) {
      navigateToNextStep(navigate, location, 'next');
    } else {
      navigate(`${location.pathname.split('/').slice(0, -1).join('/')}/step9`);
    }
  };
  const step8NextPage = (formikContext: any) => {
    window.scrollTo(0, 0);
    if (formikContext.values.SaveYesNo === 'true') {
      updatePartialFormData(formikContext);
    }
    if (formikContext.values.isDebtorTypeIndividual) {
      if (formikContext.values.receiveCenterlinkPayments === 'true' && formikContext.values.instalmentDeducted === 'true') {
        navigate(`${location.pathname.split('/').slice(0, -1).join('/')}/step10`);
      } else {
        navigateToNextStep(navigate, location, 'next');
      }
    } else {
      navigateToNextStep(navigate, location, 'next');
    }
  };
  const saveAndContinue = (
    formikContext: any
    ) => {
    window.scrollTo(0, 0);
    if (formikContext.values.SaveYesNo === 'true') {
      updatePartialFormData(formikContext);
    }
    navigateToNextStep(navigate, location, 'next');
  };

  const formNavActions: iFormNavActions = {
    step1: {
      next: postObligation,
    },
    step3: {
      next: validateDebtor,
    },
    step4: {
      next: createPartialForm,
    },
    step5: {
      next: saveAndContinue,
    },
    step6: {
      next: step6NextPage,
    },
    step7: {
      next: step7NextPage,
    },
    step8: {
      next: step8NextPage,
    },
    step9: {
      next: saveAndContinue,
    },
    step10: {
      next: saveAndContinue,
    },
    step11: {
      next: saveAndContinue,
    },
    step12: {
      next: saveAndContinue,
    },
    step13: {
      next: saveAndContinue,
    },
    // step14: {
    //   next: saveAndContinue,
    // },
  };

  const clearApiError = () => {
    setApiError({
      Error: '',
    });
  };

  const displayErrorsCount = (errorCount: number, touchedFields: number): void => {
    if (touchedFields === 0) {
      setActiveError(true);
    } else if (errorCount > 0) {
      setActiveError(true);
    } else {
      setActiveError(false);
    }
  };

  const createPartialFormForSumbit = async (formValues: iInstalmentFormValues): Promise<string> => {
    formikValuesRef.current.setFieldValue('SaveYesNo', 'true');
    resetPFormDataValues(formikValuesRef.current.values, formValues.FormData);
    const payload = {
      Attachments: '',
      CreateDate: '',
      DebtorID: `${formValues.DebtorID}`,
      EMRemeinderSent: false,
      Email: '',
      ExpiryDate: '',
      FormData: JSON.stringify({ ...formValues.FormData, SaveYesNo: 'true' }),
      FormTypeID: 1,
      ID: 0,
      IsSubscribedEM: false,
      IsSubscribedSMS: false,
      Mobile: '',
      Obligations: [
        {
          ID: 0,
          ObligationNO: formValues.OBLIGATION_NUMBER,
          IsPrimary: true,
        },
      ],
      SMSRemeinderSent: false,
    };
    const url = `${API_URL}sitecore/PartialForms/CreatePartialForm`;
    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      if (responseBody.status === 200) {
          return 'true';
      }
      return responseBody;
    } catch (error) {
      return `${error}`;
    }
  };

  const submitForm = async (formValues: iInstalmentFormValues): Promise<boolean> => {
    // const submissionNumber = '234';
    const formSubmissionValues = getSubmissionPayload(formValues);
    const submitPayload = {
      data: JSON.stringify(formSubmissionValues),
      obligationNo: formValues.OBLIGATION_NUMBER
    };

    const urlSubmission = formValues.isDebtorTypeCompany ? `${API_URL}sitecore/Instalment/SubmitPaymentArrangementCompany` : `${API_URL}sitecore/Instalment/SubmitPaymentArrangementIndividual`;
    let submitResponse = false;
      setLoading(true);
      submitResponse = await fetch(urlSubmission, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitPayload),
      })
      .then((formSubResponse) => {
        if (formSubResponse.status === 200) {
          if (formValues.SaveYesNo === 'true') {
            // having partial form, close it
            submitSavedPartialForm(formValues.PartialFormID);
          }
          formikValuesRef.current.setFieldValue('submitted', true);
          ReactGA.event({
            category: 'Instalment Form',
            action: 'Submit',
            label: 'Instalment Form',
          });
          // navigate to submission success
          return formSubResponse.json();
        }
        formikValuesRef.current.setFieldValue('submissionError', formSubResponse.statusText);
        if (formValues.SaveYesNo === 'false') {
          createPartialFormForSumbit(formValues);
        }
        navToError(navigate, location);
        return false;
      }).then((response) => {
        if (response) {
          if (response.receiptNo) {
            formikValuesRef.current.setFieldValue('submissionNo', response.receiptNo); // get submission number from API response
            return true;
          }
          navToError(navigate, location);
          return false;
        }
        return false;
      }).catch((error) => {
        formikValuesRef.current.setFieldValue('submissionError', error);
        if (formValues.SaveYesNo === 'false') {
          createPartialFormForSumbit(formValues);
        }
        console.log(error);
        navToError(navigate, location);
        return false;
      })
      .finally(() => {
        setLoading(false);
      });
      if (!submitResponse) return false;
      return true;
  };

  return (
    <Formik
      initialValues={defaultForm()}
      innerRef={formikValuesRef}
      validationSchema={InstalmentSchema}
      onSubmit={(values: iInstalmentFormValues) => {
        submitForm(values);
      }}>
      {({ values }) => (
        <VicFinesContext.Provider value={{ countryList, setCountryList, tooltips, setTooltips, apiError, clearApiError }}>
          <FormContainer title="Pay by instalments">
            {loading && <FormLoading />}
            {!loading && (
              <Form>
                <ProgressBar formMetaData={InstalmentMetaData} />
                {activeError && <Alert title="Sorry there is a problem" icon={IssueIcon} color="red" message={<ErrorContent />} theme="error" />}
                <Routes>
                  <Route path="/step0/:ob" element={<InstalmentStep0 formikRef={formikValuesRef} />} />
                  <Route path="/step1" element={<InstalmentStep1 />} />
                  <Route path="/step2" element={<InstalmentStep2 />} />
                  <Route path="/step3" element={<InstalmentStep3 />} />
                  <Route path="/step4" element={<InstalmentStep4 />} />
                  <Route path="/step5" element={<InstalmentStep5 />} />
                  <Route path="/step6" element={<InstalmentStep6 />} />
                  <Route path="/step7" element={<InstalmentStep7 />} />
                  <Route path="/step8" element={<InstalmentStep8 />} />
                  <Route path="/step9" element={<InstalmentStep9 />} />
                  <Route path="/step10" element={<InstalmentStep10 />} />
                  <Route path="/step11" element={<InstalmentStep11 />} />
                  <Route path="/step12" element={<InstalmentStep12 />} />
                  <Route path="/step13" element={<InstalmentStep13 />} />
                  <Route path="/step14" element={<InstalmentStep14 />} />
                  <Route path="/step15" element={<InstalmentStep15 />} />
                  <Route path="/error" element={<InstalmentErrorPage />} />
                  <Route path="/" element={<InstalmentStep0 formikRef={formikValuesRef} />} />
                </Routes>

                {!(location.pathname === `${PATH_INSTALMENT}/step15` || location.pathname === `${PATH_INSTALMENT}/error`) && (
                  <FormNavFooter
                    totalPages={15}
                    formNavActions={formNavActions}
                    submitPage={14}
                    submit={async () => submitForm(values)}
                    tooltips={tooltips}
                    displayErrorsCount={displayErrorsCount}
                    pageValidationKeys={InstalmentMetaData.pagesSegments[currentStep].validationKeys(values)}
                    formType={FormType.INSTALMENT}
                    firstPageNo={0}
                  />
                )}
              </Form>
            )}
          </FormContainer>
        </VicFinesContext.Provider>
      )}
    </Formik>
  );
}

export default Instalment;
